import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { DatePicker, Select, Input, Button, Modal, message, Spin, TimePicker, Checkbox } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import 'antd/dist/reset.css';
import { fetchEquipmentList, fetchPhysicalLogActivities, savePhysicalLogActivity, updatePhysicalLogActivity, deletePhysicalLogActivity } from './api';

const { TextArea } = Input;

// Hour grid constants - responsive to fill viewport
const HOURS_PER_DAY = 24;
const TOTAL_DAYS = 7;
const TOTAL_HOURS = HOURS_PER_DAY * TOTAL_DAYS; // 168 hours
const EQUIPMENT_LABEL_WIDTH = 120; // fixed width for equipment names
// Day width as percentage of grid area (100% / 7 days)
const DAY_WIDTH_PERCENT = 100 / TOTAL_DAYS; // ~14.2857%
// Hour width as percentage of grid area
const HOUR_WIDTH_PERCENT = 100 / TOTAL_HOURS; // ~0.5952%
// Split pending highlight (PACKING manufacturing with is_split_completed = false)
const SPLIT_PENDING_COLOR = '#facc15';
const SPLIT_PENDING_STRIPES = 'repeating-linear-gradient(135deg, rgba(250, 204, 21, 0.45) 0 5px, transparent 5px 10px)';

const PhysicalLogEntry = () => {
  const [equipmentList, setEquipmentList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(dayjs()); // Default to today
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  
  // Read-only detail modal state
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Ref to track if duration change is from end_time update (to prevent circular updates)
  const durationFromEndTimeRef = useRef(false);
  
  // Download modal state
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [downloadStartDate, setDownloadStartDate] = useState(null);
  const [downloadEndDate, setDownloadEndDate] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  
  // Form state - simplified for Physical Log Entry (no volume, no batch generation, no scheduling modes)
  const [formData, setFormData] = useState({
    equipment_id: null,
    equipment_name: '',
    activity_type: 'manufacturing',
    start_time: null,
    end_time: null,
    start_date: null,
    start_time_only: null,
    end_date: null,
    end_time_only: null,
    duration_hrs: '',
    batch_number: '',
    product_name: '',
    cleaning_type: null,
    maintenance_type: null,
    remarks: '',
    // Compression machine specific fields
    first_good_tablet_date: null,
    first_good_tablet_time: null,
    last_good_tablet_date: null,
    last_good_tablet_time: null,
    // Packing machine specific field (default checked)
    is_split_completed: true
  });
  
  // Refs for scroll sync
  const headerScrollRef = useRef(null);
  const contentScrollRef = useRef(null);
  const fixedScrollRef = useRef(null);
  
  // Sync horizontal scroll
  const handleContentScroll = (e) => {
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = e.target.scrollLeft;
    if (fixedScrollRef.current) fixedScrollRef.current.scrollLeft = e.target.scrollLeft;
  };
  
  const handleHeaderScroll = (e) => {
    if (contentScrollRef.current) contentScrollRef.current.scrollLeft = e.target.scrollLeft;
    if (fixedScrollRef.current) fixedScrollRef.current.scrollLeft = e.target.scrollLeft;
  };
  
  const handleFixedScroll = (e) => {
    if (contentScrollRef.current) contentScrollRef.current.scrollLeft = e.target.scrollLeft;
    if (headerScrollRef.current) headerScrollRef.current.scrollLeft = e.target.scrollLeft;
  };

  // Activity type options
  const activityTypes = [
    { value: 'manufacturing', label: 'Manufacturing', color: '#3b82f6' },
    { value: 'cleaning', label: 'Cleaning', color: '#10b981' },
    { value: 'maintenance', label: 'Maintenance', color: '#f59e0b' }
  ];

  const cleaningTypes = [
    { value: 'Type-A', label: 'Type-A' },
    { value: 'Type-B', label: 'Type-B' },
    { value: 'Type-C', label: 'Type-C' }
  ];

  const maintenanceTypes = [
    { value: 'PMP', label: 'PMP' },
    { value: 'Breakdown', label: 'Breakdown' },
    { value: 'Qualification/Validation', label: 'Qualification/Validation' }
  ];

  // Helper function to check if equipment is a compression machine
  const isCompressionMachine = (equipmentId) => {
    if (!equipmentId) return false;
    // Find equipment from equipmentList by equipment_id
    const equipment = equipmentList.find(eq => 
      String(eq.equipment_id || eq.id) === String(equipmentId)
    );
    if (!equipment) return false;
    const equipmentName = equipment.equipment_name || equipment.name || '';
    return String(equipmentName).toUpperCase().startsWith('COMP');
  };

  // Helper function to check if equipment is in the PACKING area (shows "Is Split Completed")
  const isPackingEquipment = (equipmentId) => {
    if (!equipmentId) return false;
    const equipment = equipmentList.find(eq =>
      String(eq.equipment_id || eq.id) === String(equipmentId)
    );
    if (!equipment) return false;
    return String(equipment.location_area || '').trim().toUpperCase() === 'PACKING';
  };

  // Fetch equipment list on mount
  useEffect(() => {
    const loadEquipmentList = async () => {
      try {
        setLoading(true);
        const response = await fetchEquipmentList();
        setEquipmentList(response.data || []);
      } catch (err) {
        console.error('Error loading equipment list:', err);
      } finally {
        setLoading(false);
      }
    };
    loadEquipmentList();
  }, []);

  // Calculate week start (Monday) and end (Sunday) based on selected date
  const weekStart = useMemo(() => {
    try {
      const validDate = dayjs(selectedDate).isValid() ? selectedDate : dayjs();
      const start = validDate.startOf('week');
      return start;
    } catch (error) {
      console.error('[Safari] Error calculating weekStart:', error);
      return dayjs().startOf('week');
    }
  }, [selectedDate]);

  const weekEnd = useMemo(() => {
    try {
      return weekStart.endOf('week');
    } catch (error) {
      console.error('[Safari] Error calculating weekEnd:', error);
      return dayjs().endOf('week');
    }
  }, [weekStart]);

  // Load activities for the week (call API for each day)
  const loadActivities = useCallback(async () => {
    try {
      setLoadingActivities(true);
      const allWeekActivities = [];
      
      // Fetch activities for each day in the week (7 days)
      for (let i = 0; i < 7; i++) {
        const currentDate = weekStart.add(i, 'day');
        const dateStr = currentDate.format('DD-MM-YYYY'); // API expects DD-MM-YYYY format
        
        try {
          const response = await fetchPhysicalLogActivities(dateStr);
          // API returns 'activities' array, not 'data'
          const activitiesData = response.activities || [];
          
          if (activitiesData.length > 0) {
            // Transform the API response - fields are already in expected format
            const transformedActivities = activitiesData.map(item => {
              return {
                id: item.id,
                equipment_id: item.equipment_id,
                equipment_name: item.equipment_name || item.equipment_id,
                eqp_id: item.equipment_id,
                activity_type: item.activity_type || 'manufacturing',
                batch_number: item.batch_number || null,
                product_name: item.product_name || '',
                cleaning_type: item.cleaning_type || null,
                maintenance_type: item.maintenance_type || null,
                start_time: item.start_time,
                end_time: item.end_time,
                remarks: item.remarks || '',
                // Compression machine specific fields
                first_good_tablet: item.first_good_tablet || null,
                last_good_tablet: item.last_good_tablet || null,
                // Packing machine specific field
                is_split_completed: item.is_split_completed !== false
              };
            });
            allWeekActivities.push(...transformedActivities);
          }
        } catch (err) {
          console.error(`Error loading activities for ${dateStr}:`, err);
        }
      }
      
      // Remove duplicates based on ID
      const uniqueActivities = [];
      const seenIds = new Set();
      for (const activity of allWeekActivities) {
        if (activity.id && !seenIds.has(activity.id)) {
          seenIds.add(activity.id);
          uniqueActivities.push(activity);
        }
      }
      
      setActivities(uniqueActivities);
    } catch (err) {
      console.error('Error loading activities:', err);
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  }, [weekStart]);

  // Load activities when date changes
  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  // Calculate display range for the week
  const displayStartTime = useMemo(() => {
    try {
      const start = weekStart.startOf('day');
      return start.toDate();
    } catch (error) {
      console.error('[Safari] Error calculating displayStartTime:', error);
      return dayjs().startOf('week').startOf('day').toDate();
    }
  }, [weekStart]);

  const displayEndTime = useMemo(() => {
    try {
      const end = weekEnd.endOf('day');
      return end.toDate();
    } catch (error) {
      console.error('[Safari] Error calculating displayEndTime:', error);
      return dayjs().endOf('week').endOf('day').toDate();
    }
  }, [weekEnd]);

  // Calculate activity position and width for each day block
  const getActivityBlocks = useCallback((equipmentIdentifier) => {
    const startTime = displayStartTime;
    const endTime = displayEndTime;

    const filtered = activities.filter(activity => {
      const matchesEquipment = 
        String(activity.equipment_name || '') === String(equipmentIdentifier || '') || 
        String(activity.equipment_id || '') === String(equipmentIdentifier || '') ||
        String(activity.eqp_id || '') === String(equipmentIdentifier || '');
      
      if (!matchesEquipment) {
        return false;
      }
      
      try {
        let activityStart, activityEnd;
        
        if (activity.start_time) {
          let parsedStart = dayjs(activity.start_time);
          if (parsedStart.isValid()) {
            activityStart = parsedStart.toDate();
          } else {
            activityStart = new Date(activity.start_time);
            if (isNaN(activityStart.getTime())) {
              return false;
            }
          }
        } else {
          return false;
        }
        
        if (activity.end_time) {
          let parsedEnd = dayjs(activity.end_time);
          if (parsedEnd.isValid()) {
            activityEnd = parsedEnd.toDate();
          } else {
            activityEnd = new Date(activity.end_time);
            if (isNaN(activityEnd.getTime())) {
              // Cap ongoing activities at current time, not end of week
              const now = new Date();
              activityEnd = now < endTime ? now : endTime;
            }
          }
        } else {
          // Cap ongoing activities at current time, not end of week
          const now = new Date();
          activityEnd = now < endTime ? now : endTime;
        }
        
        const isInRange = activityStart <= endTime && activityEnd >= startTime;
        return isInRange;
      } catch (e) {
        console.error('Error parsing activity dates:', e, activity);
        return false;
      }
    });
    
    const dayWidth = 100 / 7;
    
    // Week start time in ms for pixel calculations
    const weekStartMs = startTime.getTime();
    const msPerHour = 60 * 60 * 1000;
    
    return filtered
      .map(activity => {
        let activityStart, activityEnd;
        
        if (activity.start_time) {
          const parsedStart = dayjs(activity.start_time);
          activityStart = parsedStart.isValid() ? parsedStart.toDate() : new Date(activity.start_time);
          if (isNaN(activityStart.getTime())) {
            return null;
          }
        } else {
          return null;
        }
        
        if (activity.end_time) {
          const parsedEnd = dayjs(activity.end_time);
          activityEnd = parsedEnd.isValid() ? parsedEnd.toDate() : new Date(activity.end_time);
          if (isNaN(activityEnd.getTime())) {
            // Cap ongoing activities at current time, not end of week
            const now = new Date();
            activityEnd = now < endTime ? now : endTime;
          }
        } else {
          // Cap ongoing activities at current time, not end of week
          const now = new Date();
          activityEnd = now < endTime ? now : endTime;
        }
        
        // Clamp to visible range
        const clampedStart = Math.max(activityStart.getTime(), weekStartMs);
        const clampedEnd = Math.min(activityEnd.getTime(), endTime.getTime());
        
        // Calculate percentage-based position from week start
        const hoursFromStart = (clampedStart - weekStartMs) / msPerHour;
        const durationHours = (clampedEnd - clampedStart) / msPerHour;
        
        // Percentage positioning (relative to grid width)
        const leftPercent = (hoursFromStart / TOTAL_HOURS) * 100;
        const rawWidthPercent = (durationHours / TOTAL_HOURS) * 100;
        // Minimum bar width of 0.3% (about 15 minutes visually)
        const minBarWidthPercent = 0.3;
        // Small gap (0.1%) to prevent adjacent bars from touching
        const gapPercent = 0.1;
        const widthPercent = Math.max(minBarWidthPercent, rawWidthPercent - gapPercent);
        
        const isOngoing = !activity.end_time;
        
        // Also keep percentage for legacy/comparison (can be removed later)
        const daysFromStart = (activityStart.getTime() - startTime.getTime()) / (24 * 60 * 60 * 1000);
        const startDayIndex = Math.floor(Math.max(0, Math.min(6, daysFromStart)));
        
        return {
          ...activity,
          dayIndex: startDayIndex,
          leftPercent,
          widthPercent,
          isOngoing,
          activityStart,
          activityEnd
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.dayIndex !== b.dayIndex) {
          return a.dayIndex - b.dayIndex;
        }
        return a.activityStart.getTime() - b.activityStart.getTime();
      })
      .reduce((results, activity) => {
        let rowIndex = 0;
        const currStart = activity.activityStart.getTime();
        const currEnd = activity.activityEnd.getTime();
        
        for (let i = 0; i < results.length; i++) {
          const prev = results[i];
          const prevStart = prev.activityStart.getTime();
          const prevEnd = prev.activityEnd.getTime();
          
          if (currStart < prevEnd && currEnd > prevStart) {
            rowIndex = Math.max(rowIndex, prev.rowIndex + 1);
          }
        }
        
        results.push({ ...activity, rowIndex });
        return results;
      }, []);
  }, [activities, displayStartTime, displayEndTime]);

  // Activity type colors
  const activityColors = {
    manufacturing: '#3b82f6',
    cleaning: '#10b981',
    maintenance: '#f59e0b',
  };

  // Equipment list sorted dynamically from API using eqp_sequence
  const allEquipment = useMemo(() => {
    return equipmentList
      .map(eq => {
        const eqpSequence = eq.eqp_sequence !== undefined && eq.eqp_sequence !== null 
          ? Number(eq.eqp_sequence) 
          : null;
        
        return { 
          id: eq.equipment_id || eq.equipment_name || eq.id,
          name: eq.equipment_name || eq.name || eq.equipment_id || eq.id,
          equipmentId: eq.equipment_id || eq.equipment_name || eq.id,
          eqp_sequence: eqpSequence
        };
      })
      .sort((a, b) => {
        if (a.eqp_sequence !== null && b.eqp_sequence !== null) {
          return a.eqp_sequence - b.eqp_sequence;
        }
        if (a.eqp_sequence !== null) return -1;
        if (b.eqp_sequence !== null) return 1;
        const nameA = a?.name != null ? String(a.name) : '';
        const nameB = b?.name != null ? String(b.name) : '';
        return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
      });
  }, [equipmentList]);

  // Generate 7 days array (Monday to Sunday) - kept for reference
  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const day = weekStart.add(i, 'day');
      return {
        index: i,
        date: day,
        dayName: day.format('ddd'),
        dayNumber: day.format('DD'),
        month: day.format('MMM'),
        fullDate: day.format('YYYY-MM-DD')
      };
    });
  }, [weekStart]);

  // Generate 168 hours array (7 days × 24 hours) for hour-based grid
  const hours = useMemo(() => {
    return Array.from({ length: TOTAL_HOURS }, (_, i) => {
      const dayIndex = Math.floor(i / HOURS_PER_DAY);
      const hour = i % HOURS_PER_DAY;
      const dateTime = weekStart.add(dayIndex, 'day').hour(hour).minute(0).second(0);
      const isFirstHourOfDay = hour === 0;
      const isMajorHour = hour % 6 === 0; // 00, 06, 12, 18
      
      return {
        index: i,
        dayIndex,
        hour,
        dateTime,
        isFirstHourOfDay,
        isMajorHour,
        label: hour.toString().padStart(2, '0'),
        dayLabel: dateTime.format('ddd DD MMM'),
        fullDateTime: dateTime.format('YYYY-MM-DD HH:mm')
      };
    });
  }, [weekStart]);

  // Handle cell click - open modal for adding new activity
  const handleCellClick = (equipment, day) => {
    const cellTime = day.date.startOf('day');
    
    setSelectedCell({ equipment, day, cellTime: cellTime.toDate() });
    
    // Find the last activity for the same equipment on the same day
    const equipmentIdentifier = equipment.id || equipment.name;
    const dayStart = day.date.startOf('day').toDate();
    const dayEnd = day.date.endOf('day').toDate();
    
    const sameDayActivities = activities.filter(activity => {
      const matchesEquipment = 
        String(activity.equipment_name || '') === String(equipmentIdentifier || '') || 
        String(activity.equipment_id || '') === String(equipmentIdentifier || '') ||
        String(activity.eqp_id || '') === String(equipmentIdentifier || '');
      
      if (!matchesEquipment) return false;
      
      try {
        const activityStart = dayjs(activity.start_time).isValid() 
          ? dayjs(activity.start_time).toDate() 
          : new Date(activity.start_time);
        const activityEnd = activity.end_time 
          ? (dayjs(activity.end_time).isValid() ? dayjs(activity.end_time).toDate() : new Date(activity.end_time))
          : dayEnd;
        return activityStart <= dayEnd && activityEnd >= dayStart;
      } catch (e) {
        return false;
      }
    });
    
    let defaultStartTime = cellTime;
    if (sameDayActivities.length > 0) {
      const sortedActivities = sameDayActivities.sort((a, b) => {
        const endA = a.end_time 
          ? (dayjs(a.end_time).isValid() ? dayjs(a.end_time).toDate().getTime() : new Date(a.end_time).getTime())
          : 0;
        const endB = b.end_time 
          ? (dayjs(b.end_time).isValid() ? dayjs(b.end_time).toDate().getTime() : new Date(b.end_time).getTime())
          : 0;
        return endB - endA;
      });
      
      const lastActivity = sortedActivities[0];
      if (lastActivity.end_time) {
        defaultStartTime = dayjs(lastActivity.end_time);
      }
    }
    
    const startDate = defaultStartTime;
    setFormData({
      equipment_id: equipment.equipmentId || equipment.id,
      equipment_name: equipment.name,
      activity_type: 'manufacturing',
      start_time: startDate,
      end_time: null,
      start_date: startDate,
      start_time_only: startDate,
      end_date: null,
      end_time_only: null,
      duration_hrs: '',
      batch_number: '',
      product_name: '',
      cleaning_type: null,
      maintenance_type: null,
      remarks: '',
      first_good_tablet_date: null,
      first_good_tablet_time: null,
      last_good_tablet_date: null,
      last_good_tablet_time: null,
      is_split_completed: true
    });
    setIsModalOpen(true);
  };

  // Handle hour cell click - open modal for adding new activity at specific hour
  const handleHourCellClick = (equipment, hr) => {
    const cellTime = hr.dateTime;
    
    setSelectedCell({ equipment, hour: hr, cellTime: cellTime.toDate() });
    
    // Pre-fill form with the clicked hour
    const startDate = cellTime;
    setFormData({
      equipment_id: equipment.equipmentId || equipment.id,
      equipment_name: equipment.name,
      activity_type: 'manufacturing',
      start_time: startDate,
      end_time: null,
      start_date: startDate,
      start_time_only: startDate,
      end_date: null,
      end_time_only: null,
      duration_hrs: '',
      batch_number: '',
      product_name: '',
      cleaning_type: null,
      maintenance_type: null,
      remarks: '',
      first_good_tablet_date: null,
      first_good_tablet_time: null,
      last_good_tablet_date: null,
      last_good_tablet_time: null,
      is_split_completed: true
    });
    setIsModalOpen(true);
  };

  // Handle activity click - open read-only detail modal
  const handleActivityClick = (activity) => {
    setSelectedActivity(activity);
    setIsEditMode(false);
    setEditFormData(null);
    setIsDetailModalOpen(true);
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Combine start_date and start_time_only into start_time
      if (field === 'start_date' || field === 'start_time_only') {
        if (updated.start_date && updated.start_time_only) {
          const date = dayjs.isDayjs(updated.start_date) ? updated.start_date : dayjs(updated.start_date);
          const time = dayjs.isDayjs(updated.start_time_only) ? updated.start_time_only : dayjs(updated.start_time_only);
          if (date.isValid() && time.isValid()) {
            updated.start_time = date
              .hour(time.hour())
              .minute(time.minute())
              .second(0)
              .millisecond(0);
          }
        } else if (updated.start_date && !updated.start_time_only) {
          const date = dayjs.isDayjs(updated.start_date) ? updated.start_date : dayjs(updated.start_date);
          updated.start_time = date.isValid() ? date.startOf('day') : null;
        } else if (!updated.start_date && updated.start_time_only && updated.start_time) {
          const time = dayjs.isDayjs(updated.start_time_only) ? updated.start_time_only : dayjs(updated.start_time_only);
          const currentDate = dayjs.isDayjs(updated.start_time) ? updated.start_time : dayjs(updated.start_time);
          if (time.isValid() && currentDate.isValid()) {
            updated.start_time = currentDate
              .hour(time.hour())
              .minute(time.minute())
              .second(0)
              .millisecond(0);
            updated.start_date = updated.start_time;
          }
        }
      }
      
      // Combine end_date and end_time_only into end_time
      if (field === 'end_date' || field === 'end_time_only') {
        if (updated.end_date && updated.end_time_only) {
          const date = dayjs.isDayjs(updated.end_date) ? updated.end_date : dayjs(updated.end_date);
          const time = dayjs.isDayjs(updated.end_time_only) ? updated.end_time_only : dayjs(updated.end_time_only);
          if (date.isValid() && time.isValid()) {
            updated.end_time = date
              .hour(time.hour())
              .minute(time.minute())
              .second(0)
              .millisecond(0);
            
            // Auto-calculate duration when end_time is set and start_time exists
            if (updated.start_time && dayjs(updated.start_time).isValid()) {
              const durationHrs = dayjs(updated.end_time).diff(dayjs(updated.start_time), 'hour', true);
              if (durationHrs > 0) {
                durationFromEndTimeRef.current = true; // Mark that this change is from end_time
                updated.duration_hrs = Math.round(durationHrs * 10) / 10;
              }
            }
          }
        } else if (updated.end_date && !updated.end_time_only) {
          const date = dayjs.isDayjs(updated.end_date) ? updated.end_date : dayjs(updated.end_date);
          updated.end_time = date.isValid() ? date.endOf('day') : null;
        } else if (!updated.end_date && updated.end_time_only && updated.end_time) {
          const time = dayjs.isDayjs(updated.end_time_only) ? updated.end_time_only : dayjs(updated.end_time_only);
          const currentDate = dayjs.isDayjs(updated.end_time) ? updated.end_time : dayjs(updated.end_time);
          if (time.isValid() && currentDate.isValid()) {
            updated.end_time = currentDate
              .hour(time.hour())
              .minute(time.minute())
              .second(0)
              .millisecond(0);
            updated.end_date = updated.end_time;
            
            // Auto-calculate duration when end_time is set and start_time exists
            if (updated.start_time && dayjs(updated.start_time).isValid()) {
              const durationHrs = dayjs(updated.end_time).diff(dayjs(updated.start_time), 'hour', true);
              if (durationHrs > 0) {
                durationFromEndTimeRef.current = true; // Mark that this change is from end_time
                updated.duration_hrs = Math.round(durationHrs * 10) / 10;
              }
            }
          }
        }
      }
      
      return updated;
    });
  };

  // Auto-calculate end time from start_time + duration_hrs
  const recalcEndFromDuration = useCallback((nextDurationHrs, baseStartTime) => {
    const duration = Number(nextDurationHrs);
    if (!baseStartTime || !dayjs(baseStartTime).isValid()) return;
    if (!Number.isFinite(duration) || duration <= 0) return;

    const start = dayjs(baseStartTime);
    const end = start.add(duration, 'hour');

    setFormData(prev => ({
      ...prev,
      duration_hrs: nextDurationHrs,
      end_time: end,
      end_date: end,
      end_time_only: end,
    }));
  }, []);

  // Keep end_time in sync whenever duration_hrs changes (but skip if the change came from end_time update)
  useEffect(() => {
    if (!formData.duration_hrs) return;
    
    // Skip if the duration change came from end_time update to prevent circular updates
    if (durationFromEndTimeRef.current) {
      durationFromEndTimeRef.current = false;
      return;
    }
    
    recalcEndFromDuration(formData.duration_hrs, formData.start_time);
  }, [formData.duration_hrs, formData.start_time, recalcEndFromDuration]);

  // Reset form
  const handleReset = () => {
    if (selectedCell) {
      const startTime = dayjs(selectedCell.cellTime);
      setFormData({
        equipment_id: selectedCell.equipment.equipmentId || selectedCell.equipment.id,
        equipment_name: selectedCell.equipment.name,
        activity_type: 'manufacturing',
        start_time: startTime,
        end_time: null,
        start_date: startTime,
        start_time_only: startTime,
        end_date: null,
        end_time_only: null,
        duration_hrs: '',
        batch_number: '',
        product_name: '',
        cleaning_type: null,
        maintenance_type: null,
        remarks: '',
        first_good_tablet_date: null,
        first_good_tablet_time: null,
        last_good_tablet_date: null,
        last_good_tablet_time: null,
        is_split_completed: true
      });
    }
  };

  // Format week range for display
  const weekRangeText = useMemo(() => {
    return `${weekStart.format('DD MMM')} - ${weekEnd.format('DD MMM YYYY')}`;
  }, [weekStart, weekEnd]);

  // Format date to "YYYY-MM-DD HH:mm:ss"
  const formatDateTime = (date) => {
    if (!date) return null;
    const d = dayjs(date);
    return d.format('YYYY-MM-DD HH:mm:ss');
  };

  // Submit form
  const handleSubmit = useCallback(async () => {
    // Validation
    if (!formData.equipment_id) {
      message.error('Please select an equipment');
      return;
    }
    
    let startTime = formData.start_time;
    if (!startTime && formData.start_date) {
      if (formData.start_time_only) {
        startTime = formData.start_date
          .hour(formData.start_time_only.hour())
          .minute(formData.start_time_only.minute())
          .second(0)
          .millisecond(0);
      } else {
        startTime = formData.start_date.startOf('day');
      }
    }
    
    if (!startTime) {
      message.error('Please select a start time');
      return;
    }
    
    let endTime = formData.end_time;
    
    // If duration is provided, auto-calculate endTime
    if ((!endTime || !dayjs(endTime).isValid()) && formData.duration_hrs) {
      const duration = Number(formData.duration_hrs);
      if (!Number.isFinite(duration) || duration <= 0) {
        message.error('Please enter a valid duration (hrs)');
        return;
      }
      endTime = dayjs(startTime).add(duration, 'hour');
    }

    if (!endTime && formData.end_date) {
      if (formData.end_time_only) {
        endTime = formData.end_date
          .hour(formData.end_time_only.hour())
          .minute(formData.end_time_only.minute())
          .second(0)
          .millisecond(0);
      } else {
        endTime = formData.end_date.endOf('day');
      }
    }
    
    if (!endTime) {
      message.error('Please select an end time or enter duration');
      return;
    }
    
    // Activity-specific validation
    if (formData.activity_type === 'manufacturing') {
      if (!formData.batch_number) {
        message.error('Please enter a batch number');
        return;
      }
      if (!formData.product_name) {
        message.error('Please enter a product name');
        return;
      }
      
      // Additional validation for compression machines
      if (isCompressionMachine(formData.equipment_id)) {
        if (!formData.first_good_tablet_date || !formData.first_good_tablet_time) {
          message.error('Please enter First Good Tablet date and time');
          return;
        }
        if (!formData.last_good_tablet_date || !formData.last_good_tablet_time) {
          message.error('Please enter Last Good Tablet date and time');
          return;
        }
      }
    }
    if (formData.activity_type === 'cleaning' && !formData.cleaning_type) {
      message.error('Please select a cleaning type');
      return;
    }
    if (formData.activity_type === 'maintenance' && !formData.maintenance_type) {
      message.error('Please select a maintenance type');
      return;
    }

    setSubmitting(true);
    try {
      // Build payload based on activity type
      // API expects: eqp_id, eqp_plan_type, eqp_activity, product_batch, product_name, start_date, end_date, is_active
      let payload;
      
      if (formData.activity_type === 'manufacturing') {
        payload = {
          eqp_plan_type: 'Operation',
          eqp_activity: 'Manufacturing Activity',
          eqp_id: formData.equipment_id,
          start_date: formatDateTime(startTime),
          end_date: formatDateTime(endTime),
          product_batch: formData.batch_number || '',
          product_name: formData.product_name || '',
          // Only PACKING machines show the checkbox; everything else is always true
          is_split_completed: isPackingEquipment(formData.equipment_id) ? formData.is_split_completed : true
        };

        // Add compression machine specific fields
        if (isCompressionMachine(formData.equipment_id)) {
          const firstGoodTablet = formData.first_good_tablet_date && formData.first_good_tablet_time
            ? dayjs(formData.first_good_tablet_date)
                .hour(dayjs(formData.first_good_tablet_time).hour())
                .minute(dayjs(formData.first_good_tablet_time).minute())
                .second(0)
            : null;
          const lastGoodTablet = formData.last_good_tablet_date && formData.last_good_tablet_time
            ? dayjs(formData.last_good_tablet_date)
                .hour(dayjs(formData.last_good_tablet_time).hour())
                .minute(dayjs(formData.last_good_tablet_time).minute())
                .second(0)
            : null;
          
          if (firstGoodTablet) {
            payload.first_good_tablet = formatDateTime(firstGoodTablet);
          }
          if (lastGoodTablet) {
            payload.last_good_tablet = formatDateTime(lastGoodTablet);
          }
        }
      } else if (formData.activity_type === 'cleaning') {
        // For cleaning, eqp_activity should be the cleaning type (Type-A, Type-B, Type-C)
        payload = {
          eqp_plan_type: 'Cleaning',
          eqp_activity: formData.cleaning_type || '',
          eqp_id: formData.equipment_id,
          start_date: formatDateTime(startTime),
          end_date: formatDateTime(endTime)
        };
      } else {
        // Maintenance - eqp_activity should be the maintenance type (PMP, Breakdown)
        payload = {
          eqp_plan_type: 'Maintenance',
          eqp_activity: formData.maintenance_type || '',
          eqp_id: formData.equipment_id,
          start_date: formatDateTime(startTime),
          end_date: formatDateTime(endTime)
        };
      }

      //console.log('=== PHYSICAL LOG ENTRY SUBMISSION ===');
      //console.log('Payload:', payload);

      const response = await savePhysicalLogActivity(payload);
      
      if (response.success) {
        message.success('Activity logged successfully!');
        await loadActivities();
        handleReset();
        setIsModalOpen(false);
      } else {
        throw new Error(response.message || 'Failed to log activity');
      }
    } catch (err) {
      console.error('Error logging activity:', err);
      message.error(err.message || 'Failed to log activity. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [formData, loadActivities]);

  const getActivityColor = (type) => {
    const activity = activityTypes.find(a => a.value === type);
    return activity ? activity.color : '#6b7280';
  };

  // Download activities as Excel
  const handleDownloadActivities = useCallback(async () => {
    if (!downloadStartDate || !downloadEndDate) {
      message.error('Please select both start and end dates');
      return;
    }

    if (downloadEndDate.isBefore(downloadStartDate)) {
      message.error('End date must be after start date');
      return;
    }

    try {
      setDownloading(true);
      const allActivities = [];
      
      // Calculate total days
      const totalDays = downloadEndDate.diff(downloadStartDate, 'day') + 1;
      setDownloadProgress({ current: 0, total: totalDays });
      
      // Fetch activities for each day in the range
      for (let i = 0; i < totalDays; i++) {
        const currentDate = downloadStartDate.add(i, 'day');
        const dateStr = currentDate.format('DD-MM-YYYY');
        
        setDownloadProgress({ current: i + 1, total: totalDays });
        
        try {
          const response = await fetchPhysicalLogActivities(dateStr);
          const activitiesData = response.activities || [];
          
          if (activitiesData.length > 0) {
            allActivities.push(...activitiesData);
          }
        } catch (err) {
          console.error(`Error loading activities for ${dateStr}:`, err);
        }
      }
      
      // Remove duplicates based on ID
      const uniqueActivities = [];
      const seenIds = new Set();
      for (const activity of allActivities) {
        if (!seenIds.has(activity.id)) {
          seenIds.add(activity.id);
          uniqueActivities.push(activity);
        }
      }

      if (uniqueActivities.length === 0) {
        message.warning('No activities found for the selected date range');
        return;
      }

      // Sort by Equipment ID first, then by start_time within each equipment
      uniqueActivities.sort((a, b) => {
        const eqCmp = String(a.equipment_id || '').localeCompare(String(b.equipment_id || ''));
        if (eqCmp !== 0) return eqCmp;
        return dayjs(a.start_time).valueOf() - dayjs(b.start_time).valueOf();
      });

      // Format data for Excel
      const excelData = uniqueActivities.map(activity => {
        // Parse start and end times
        const startTime = activity.start_time ? dayjs(activity.start_time).format('DD-MM-YYYY HH:mm') : '';
        const endTime = activity.end_time ? dayjs(activity.end_time).format('DD-MM-YYYY HH:mm') : '';
        const firstGoodTablet = activity.first_good_tablet ? dayjs(activity.first_good_tablet).format('DD-MM-YYYY HH:mm') : '';
        const lastGoodTablet = activity.last_good_tablet ? dayjs(activity.last_good_tablet).format('DD-MM-YYYY HH:mm') : '';

        // Calculate time taken (HH:MM:SS)
        let timeTaken = '';
        if (activity.start_time && activity.end_time) {
          const diffSecs = dayjs(activity.end_time).diff(dayjs(activity.start_time), 'second');
          if (diffSecs >= 0) {
            const hh = String(Math.floor(diffSecs / 3600)).padStart(2, '0');
            const mm = String(Math.floor((diffSecs % 3600) / 60)).padStart(2, '0');
            const ss = String(diffSecs % 60).padStart(2, '0');
            timeTaken = `${hh}:${mm}:${ss}`;
          }
        }

        // Determine activity subtype
        let activitySubtype = '';
        if (activity.activity_type === 'manufacturing') {
          activitySubtype = 'Production';
        } else if (activity.activity_type === 'cleaning') {
          activitySubtype = activity.cleaning_type || '';
        } else if (activity.activity_type === 'maintenance') {
          activitySubtype = activity.maintenance_type || '';
        }

        return {
          'Equipment ID': activity.equipment_id || '',
          'Equipment Name': activity.equipment_name || '',
          'Activity Type': activity.activity_type ? activity.activity_type.charAt(0).toUpperCase() + activity.activity_type.slice(1) : '',
          'Activity Subtype': activitySubtype,
          'Batch Number': activity.batch_number || '',
          'Product Name': activity.product_name || '',
          'Start Time': startTime,
          'End Time': endTime,
          'Time Taken': timeTaken,
          'First Good Tablet': firstGoodTablet,
          'Last Good Tablet': lastGoodTablet
        };
      });
      
      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 15 },  // Equipment ID
        { wch: 20 },  // Equipment Name
        { wch: 15 },  // Activity Type
        { wch: 20 },  // Activity Subtype
        { wch: 15 },  // Batch Number
        { wch: 20 },  // Product Name
        { wch: 18 },  // Start Time
        { wch: 18 },  // End Time
        { wch: 12 },  // Time Taken
        { wch: 18 },  // First Good Tablet
        { wch: 18 }   // Last Good Tablet
      ];
      
      XLSX.utils.book_append_sheet(wb, ws, 'Physical Log Activities');
      
      // Generate filename with date range
      const startStr = downloadStartDate.format('DDMMYYYY');
      const endStr = downloadEndDate.format('DDMMYYYY');
      const filename = `Physical_Log_Activities_${startStr}_to_${endStr}.xlsx`;
      
      // Download file
      XLSX.writeFile(wb, filename);
      
      message.success(`Successfully downloaded ${uniqueActivities.length} activities`);
      setIsDownloadModalOpen(false);
      setDownloadStartDate(null);
      setDownloadEndDate(null);
    } catch (error) {
      console.error('Error downloading activities:', error);
      message.error('Failed to download activities. Please try again.');
    } finally {
      setDownloading(false);
      setDownloadProgress({ current: 0, total: 0 });
    }
  }, [downloadStartDate, downloadEndDate]);

  if (loading && equipmentList.length === 0) {
    return (
      <div style={{
        padding: '20px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        backgroundColor: '#f9fafb',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px', fontSize: '16px', color: '#6b7280' }}>
            Loading equipment schedule...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '20px', 
      fontFamily: 'system-ui, -apple-system, sans-serif',
      backgroundColor: '#f9fafb',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ 
        marginBottom: '24px',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 16px 0' }}>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
            📋 Physical Log Entry
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
       
            <a 
              href="https://staging.nucleus.plus" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                color: '#3b82f6',
                fontSize: '14px',
                fontWeight: '500',
                textDecoration: 'none'
              }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              Staging Nucleus →
            </a>
          </div>
        </div>
        
        {/* Instructions */}
        <div style={{ 
          padding: '12px 16px',
          backgroundColor: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '6px',
          marginBottom: '16px'
        }}>
          <div style={{ fontSize: '14px', color: '#1e40af', fontWeight: '500' }}>
            💡 Click on any cell to log actual processed activity for that equipment and day
          </div>
        </div>
        
        {/* Date Selection */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>Select Date:</label>
            <DatePicker
              value={selectedDate}
              onChange={(date) => {
                if (date) {
                  setSelectedDate(date);
                }
              }}
              format="DD-MMM-YYYY"
              allowClear={false}
              style={{ width: 150 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button 
              size="small"
              onClick={() => {
                const newDate = weekStart.subtract(1, 'week');
                setSelectedDate(newDate);
              }}
              style={{ fontSize: '13px' }}
            >
              ← Previous Week
            </Button>
            <Button 
              size="small"
              onClick={() => {
                const today = dayjs();
                const monday = today.startOf('week');
                setSelectedDate(monday);
              }}
              style={{ fontSize: '13px' }}
            >
              This Week
            </Button>
            <Button 
              size="small"
              onClick={() => {
                const newDate = weekStart.add(1, 'week');
                setSelectedDate(newDate);
              }}
              style={{ fontSize: '13px' }}
            >
              Next Week →
            </Button>
          </div>
          <div style={{ color: '#6b7280', fontSize: '14px' }}>
            Showing week: {weekRangeText}
          </div>
          {loadingActivities && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6b7280', fontSize: '14px' }}>
              <Spin size="small" />
              <span>Loading activities...</span>
            </div>
          )}

      <div style={{ marginLeft: 'auto' }}>
                <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => {
                setDownloadStartDate(weekStart);
                setDownloadEndDate(weekEnd);
                setIsDownloadModalOpen(true);
              }}
              style={{ marginRight: '8px' }}
            >
              Download Excel
            </Button>
       </div>
        </div>

        {/* Legend */}
        <div style={{ 
          display: 'flex', 
          gap: '20px', 
          marginTop: '16px',
          paddingTop: '16px',
          borderTop: '1px solid #e5e7eb',
          flexWrap: 'wrap'
        }}>
          {activityTypes.map(type => (
            <div key={type.value} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: type.color,
                borderRadius: '4px'
              }} />
              <span style={{ fontSize: '14px' }}>{type.label}</span>
            </div>
          ))}
          {/* Compression machine markers legend */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              width: '20px', 
              height: '20px', 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f0f0f0',
              borderRadius: '4px'
            }}>
              <div style={{
                width: '3px',
                height: '16px',
                backgroundColor: '#991b1b',
                borderRadius: '1px'
              }} />
            </div>
            <span style={{ fontSize: '14px' }}>First / Last Good Tablet</span>
          </div>
          {/* Split pending legend (packing machines) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '20px',
              height: '20px',
              backgroundColor: activityColors.manufacturing,
              backgroundImage: SPLIT_PENDING_STRIPES,
              outline: `2px solid ${SPLIT_PENDING_COLOR}`,
              outlineOffset: '-2px',
              borderRadius: '4px'
            }} />
            <span style={{ fontSize: '14px' }}>Split Pending</span>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        position: 'relative'
      }}>
        {/* Sticky Header Row */}
        <div 
          ref={headerScrollRef}
          onScroll={handleHeaderScroll}
          className="header-scroll-hidden"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'white',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
            overflowX: 'auto',
            overflowY: 'visible'
          }}
        >
          <div style={{ 
            display: 'flex', 
            borderBottom: '2px solid #e5e7eb',
            width: '100%'
          }}>
            <div style={{ 
              width: `${EQUIPMENT_LABEL_WIDTH}px`, 
              minWidth: `${EQUIPMENT_LABEL_WIDTH}px`,
              padding: '12px',
              fontWeight: '600',
              backgroundColor: '#f9fafb',
              position: 'sticky',
              left: 0,
              zIndex: 110,
              borderRight: '2px solid #e5e7eb',
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end'
            }}>
              Equipment
            </div>
            <div style={{ display: 'flex', flex: 1 }}>
              {days.map(day => {
                const isToday = day.date.isSame(dayjs(), 'day');
                return (
                  <div
                    key={day.index}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      boxSizing: 'border-box',
                      padding: '4px 2px',
                      textAlign: 'center',
                      fontSize: '10px',
                      fontWeight: '500',
                      backgroundColor: isToday ? '#fee2e2' : (day.index % 2 === 0 ? '#f9fafb' : '#ffffff'),
                      borderLeft: day.index === 0 ? 'none' : '1px solid #d1d5db',
                      borderRight: day.index === 6 ? '1px solid #d1d5db' : 'none'
                    }}
                  >
                    <div style={{ fontWeight: '600', color: isToday ? '#991b1b' : '#374151', fontSize: '11px' }}>
                      {day.dayName}
                    </div>
                    <div style={{ fontSize: '9px', color: '#6b7280' }}>
                      {day.dayNumber} {day.month}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Scrollable Equipment Rows */}
        <div 
          ref={contentScrollRef}
          onScroll={handleContentScroll}
          className="content-scroll-hidden"
          style={{ overflowX: 'auto' }}
        >
          <div style={{ width: '100%', position: 'relative' }}>
            {/* Equipment Rows */}
            {allEquipment.length === 0 ? (
              <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
                No equipment data available
              </div>
            ) : (
              allEquipment.map((equipment) => {
                const eqBlocks = getActivityBlocks(equipment.id || equipment.name);
                const eqMaxRowIndex = eqBlocks.reduce((max, b) => Math.max(max, b.rowIndex || 0), 0);
                const eqRowHeight = 22;
                const eqPadding = 6;
                const eqDynamicHeight = Math.max(50, (eqMaxRowIndex + 1) * eqRowHeight + eqPadding * 2);
                
                return (
                <div
                  key={equipment.id}
                  data-equipment-row={equipment.id || equipment.name}
                  style={{
                    display: 'flex',
                    borderBottom: '1px solid #e5e7eb',
                    position: 'relative',
                    minHeight: `${eqDynamicHeight}px`,
                    width: '100%'
                  }}
                >
                  {/* Equipment Name */}
                  <div style={{
                    width: `${EQUIPMENT_LABEL_WIDTH}px`,
                    minWidth: `${EQUIPMENT_LABEL_WIDTH}px`,
                    padding: '8px 12px',
                    fontWeight: '500',
                    fontSize: '13px',
                    backgroundColor: '#f9fafb',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'sticky',
                    left: 0,
                    zIndex: 10,
                    borderRight: '2px solid #e5e7eb',
                    flexShrink: 0
                  }}>
                    {equipment.name}
                  </div>

                  {/* Hour Blocks - Clickable cells grouped by day */}
                  <div style={{ position: 'relative', flex: 1, display: 'flex', zIndex: 1 }}>
                    {days.map(day => {
                      const isToday = day.date.isSame(dayjs(), 'day');
                      return (
                        <div
                          key={day.index}
                          onClick={(e) => {
                            if (!e.target.closest('[data-activity-card]')) {
                              handleCellClick(equipment, day);
                            }
                          }}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            boxSizing: 'border-box',
                            borderLeft: day.index === 0 ? 'none' : '1px solid #e5e7eb',
                            borderRight: day.index === 6 ? '1px solid #e5e7eb' : 'none',
                            backgroundColor: isToday ? '#fef2f2' : (day.index % 2 === 0 ? '#ffffff' : '#fafafa'),
                            cursor: 'pointer',
                            transition: 'background-color 0.15s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e0f2fe';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = isToday ? '#fef2f2' : (day.index % 2 === 0 ? '#ffffff' : '#fafafa');
                          }}
                          title={`Click to log activity for ${equipment.name} on ${day.date.format('ddd DD MMM YYYY')}`}
                        />
                      );
                    })}

                    {/* Activity Blocks */}
                    {eqBlocks.map(activity => {
                      const baseColor = activityColors[activity.activity_type] || '#6b7280';
                      // Packing manufacturing activity whose split is not completed yet
                      const isSplitPending = activity.activity_type === 'manufacturing' && activity.is_split_completed === false;
                      const ongoingGradient = activity.isOngoing ? 'linear-gradient(90deg, transparent 80%, rgba(255,255,255,0.3) 100%)' : null;

                      const verticalOffset = (activity.rowIndex || 0) * eqRowHeight;
                      const cellHeight = eqDynamicHeight;
                      const maxHeight = cellHeight - (eqPadding * 2);
                      const availableHeight = maxHeight - verticalOffset;
                      const activityHeight = Math.min(eqRowHeight, Math.max(18, availableHeight));
                      
                      // Calculate marker positions for compression machines (first/last good tablet)
                      // Use CLAMPED values since the bar represents the visible portion of the activity
                      let firstGoodTabletPercent = null;
                      let lastGoodTabletPercent = null;
                      
                      // Get clamped start/end (what the bar actually represents)
                      const weekStartMs = weekStart.toDate().getTime();
                      const weekEndMs = weekEnd.toDate().getTime();
                      const clampedStart = Math.max(activity.activityStart.getTime(), weekStartMs);
                      const clampedEnd = Math.min(activity.activityEnd.getTime(), weekEndMs);
                      const clampedDuration = clampedEnd - clampedStart;
                      
                      if (activity.first_good_tablet && clampedDuration > 0) {
                        const firstGoodTime = dayjs(activity.first_good_tablet).valueOf();
                        // Only show marker if it falls within the visible (clamped) range
                        if (firstGoodTime >= clampedStart && firstGoodTime <= clampedEnd) {
                          firstGoodTabletPercent = ((firstGoodTime - clampedStart) / clampedDuration) * 100;
                        }
                      }
                      
                      if (activity.last_good_tablet && clampedDuration > 0) {
                        const lastGoodTime = dayjs(activity.last_good_tablet).valueOf();
                        // Only show marker if it falls within the visible (clamped) range
                        if (lastGoodTime >= clampedStart && lastGoodTime <= clampedEnd) {
                          lastGoodTabletPercent = ((lastGoodTime - clampedStart) / clampedDuration) * 100;
                        }
                      }
                      
                      return (
                        <div
                          key={activity.id}
                          data-activity-card="true"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleActivityClick(activity);
                          }}
                          style={{
                            position: 'absolute',
                            left: `${activity.leftPercent}%`,
                            width: `${activity.widthPercent}%`,
                            top: `${eqPadding + verticalOffset}px`,
                            height: `${activityHeight}px`,
                            backgroundColor: baseColor,
                            backgroundImage: [isSplitPending ? SPLIT_PENDING_STRIPES : null, ongoingGradient].filter(Boolean).join(', ') || 'none',
                            outline: isSplitPending ? `2px solid ${SPLIT_PENDING_COLOR}` : 'none',
                            outlineOffset: '-2px',
                            borderRadius: '3px',
                            padding: '2px 4px',
                            color: 'white',
                            fontSize: '10px',
                            overflow: 'visible',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10,
                            boxSizing: 'border-box',
                            pointerEvents: 'auto'
                          }}
                          title={`${activity.activity_type} - ${activity.batch_number || activity.cleaning_type || activity.maintenance_type || ''}${activity.end_time ? ` (${Math.round(dayjs(activity.end_time).diff(dayjs(activity.start_time), 'hour', true) * 10) / 10}hrs)` : ''} | ${dayjs(activity.start_time).format('DD MMM HH:mm')} - ${activity.end_time ? dayjs(activity.end_time).format('DD MMM HH:mm') : 'Ongoing'}${activity.first_good_tablet ? ` | First Good: ${dayjs(activity.first_good_tablet).format('DD MMM HH:mm')}` : ''}${activity.last_good_tablet ? ` | Last Good: ${dayjs(activity.last_good_tablet).format('DD MMM HH:mm')}` : ''}${isSplitPending ? ' | Split Pending' : ''} - Click to view details`}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.zIndex = 10;
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.zIndex = 5;
                            e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.2)';
                          }}
                        >
                          {/* First Good Tablet Marker - Dark red vertical line */}
                          {firstGoodTabletPercent !== null && (
                            <div
                              style={{
                                position: 'absolute',
                                left: `${firstGoodTabletPercent}%`,
                                top: '-3px',
                                bottom: '-3px',
                                transform: 'translateX(-50%)',
                                width: '2px',
                                backgroundColor: '#991b1b',
                                zIndex: 15,
                                boxShadow: '0 0 2px rgba(0,0,0,0.4)'
                              }}
                              title={`First Good Tablet: ${dayjs(activity.first_good_tablet).format('DD MMM YYYY HH:mm')}`}
                            />
                          )}
                          
                          {/* Last Good Tablet Marker - Dark red vertical line */}
                          {lastGoodTabletPercent !== null && (
                            <div
                              style={{
                                position: 'absolute',
                                left: `${lastGoodTabletPercent}%`,
                                top: '-3px',
                                bottom: '-3px',
                                transform: 'translateX(-50%)',
                                width: '2px',
                                backgroundColor: '#991b1b',
                                zIndex: 15,
                                boxShadow: '0 0 2px rgba(0,0,0,0.4)'
                              }}
                              title={`Last Good Tablet: ${dayjs(activity.last_good_tablet).format('DD MMM YYYY HH:mm')}`}
                            />
                          )}
                          
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '2px',
                            width: '100%',
                            height: '100%',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flex: 1,
                            overflow: 'hidden',
                            padding: '0 2px'
                          }}>
                            <div style={{ 
                              fontWeight: '600', 
                              whiteSpace: 'nowrap', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              fontSize: '10px',
                              lineHeight: '1.3',
                              textAlign: 'center',
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {activity.batch_number || activity.cleaning_type || activity.maintenance_type || activity.activity_type}
                              {activity.end_time && <span style={{ fontSize: '8px', opacity: 0.85, marginLeft: '4px' }}>({Math.round(dayjs(activity.end_time).diff(dayjs(activity.start_time), 'hour', true) * 10) / 10}hrs)</span>}
                            </div>
                            <div style={{ 
                              fontSize: '8px', 
                              opacity: 0.9,
                              whiteSpace: 'nowrap', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              lineHeight: '1.3',
                              textAlign: 'center',
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {dayjs(activity.start_time).format('DD MMM HH:mm')} - {activity.end_time ? dayjs(activity.end_time).format('DD MMM HH:mm') : 'Ongoing'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Fixed Horizontal Scrollbar at Bottom - hidden when grid fits viewport */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '24px',
          backgroundColor: '#e5e7eb',
          borderTop: '2px solid #cbd5e1',
          zIndex: 1000,
          display: 'none', // Hidden - grid now fits viewport
          alignItems: 'center',
          padding: '0 8px'
        }}
      >
        <div
          ref={fixedScrollRef}
          onScroll={handleFixedScroll}
          className="visible-scrollbar"
          style={{
            flex: 1,
            overflowX: 'scroll',
            overflowY: 'hidden'
          }}
        >
          <div style={{ width: '100%', height: '1px' }} />
        </div>
      </div>

      {/* Scrollbar styles */}
      <style>
        {`
          .header-scroll-hidden,
          .content-scroll-hidden {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .header-scroll-hidden::-webkit-scrollbar,
          .content-scroll-hidden::-webkit-scrollbar {
            display: none;
          }
          
          .visible-scrollbar {
            scrollbar-width: auto !important;
            scrollbar-color: #64748b #d1d5db !important;
          }
          .visible-scrollbar::-webkit-scrollbar {
            height: 16px !important;
            display: block !important;
          }
          .visible-scrollbar::-webkit-scrollbar-track {
            background: #d1d5db !important;
            border-radius: 8px !important;
          }
          .visible-scrollbar::-webkit-scrollbar-thumb {
            background: #64748b !important;
            border-radius: 8px !important;
            border: 3px solid #d1d5db !important;
          }
          .visible-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #475569 !important;
          }
        `}
      </style>
      
      {/* Bottom padding for fixed scrollbar */}
      <div style={{ height: '25px' }} />

      {/* Create Activity Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              width: '8px', 
              height: '24px', 
              backgroundColor: getActivityColor(formData.activity_type),
              borderRadius: '4px'
            }} />
            <span>Log New Activity</span>
          </div>
        }
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          handleReset();
        }}
        footer={null}
        width={550}
        destroyOnClose
      >
        <div style={{ padding: '16px 0' }}>
          {/* Selected Cell Info */}
          {selectedCell && (
            <div style={{ 
              padding: '12px 16px',
              backgroundColor: '#f3f4f6',
              borderRadius: '8px',
              marginBottom: '20px',
              display: 'flex',
              gap: '24px'
            }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Equipment</div>
                <div style={{ fontWeight: '600', color: '#1f2937' }}>{selectedCell.equipment.name}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Time Slot</div>
                <div style={{ fontWeight: '600', color: '#1f2937' }}>
                  {dayjs(selectedCell.cellTime).format('DD-MMM-YY HH:mm')}
                </div>
              </div>
            </div>
          )}

          {/* Activity Type Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
              Activity Type *
            </label>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {activityTypes.map(type => (
                <div
                  key={type.value}
                  onClick={() => handleChange('activity_type', type.value)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: formData.activity_type === type.value ? `2px solid ${type.color}` : '2px solid #e5e7eb',
                    backgroundColor: formData.activity_type === type.value ? `${type.color}15` : 'white',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <div style={{ width: '14px', height: '14px', borderRadius: '4px', backgroundColor: type.color }} />
                  <span style={{ 
                    fontWeight: formData.activity_type === type.value ? '600' : '500',
                    color: formData.activity_type === type.value ? type.color : '#374151',
                    fontSize: '14px'
                  }}>
                    {type.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Start & End Date Time - Two Separate Cards */}
          <div style={{ marginBottom: '20px' }}>
            {/* Row 1: Starts + Ends (always) */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch', marginBottom: formData.activity_type === 'manufacturing' && isCompressionMachine(formData.equipment_id) ? '8px' : '0' }}>
              {/* Starts Card */}
              <div style={{ 
                flex: 1,
                minWidth: 0,
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '10px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  fontSize: '12px' 
                }}>
                  Starts
                </label>
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'nowrap' }}>
                  <DatePicker
                    format="DD MMM YYYY"
                    value={
                      formData.start_date 
                        ? (dayjs.isDayjs(formData.start_date) ? formData.start_date : dayjs(formData.start_date))
                        : (formData.start_time 
                            ? (dayjs.isDayjs(formData.start_time) ? formData.start_time : dayjs(formData.start_time))
                            : null)
                    }
                    onChange={(value) => handleChange('start_date', value)}
                    style={{
                      flex: '1 1 auto',
                      minWidth: '100px',
                      maxWidth: '100%',
                      borderRadius: '4px',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      fontSize: '12px',
                      height: '32px'
                    }}
                    placeholder="Date"
                    allowClear={false}
                  />
                  <TimePicker
                    format="HH:mm"
                    value={
                      formData.start_time_only !== null && formData.start_time_only !== undefined
                        ? (dayjs.isDayjs(formData.start_time_only) 
                            ? formData.start_time_only 
                            : (formData.start_time_only ? dayjs(formData.start_time_only) : null))
                        : (formData.start_time !== null && formData.start_time !== undefined
                            ? (dayjs.isDayjs(formData.start_time) 
                                ? formData.start_time 
                                : (formData.start_time ? dayjs(formData.start_time) : null))
                            : null)
                    }
                    onChange={(value) => handleChange('start_time_only', value)}
                    minuteStep={5}
                    style={{
                      flex: '0 0 auto',
                      width: '75px',
                      borderRadius: '4px',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      fontSize: '12px',
                      height: '32px'
                    }}
                    placeholder="Time"
                    getPopupContainer={(trigger) => trigger.parentElement}
                    popupStyle={{ zIndex: 1050 }}
                    allowClear={false}
                  />
                </div>
              </div>

              {/* Ends Card */}
              <div style={{ 
                flex: 1,
                minWidth: 0,
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '10px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  fontSize: '12px' 
                }}>
                  Ends
                </label>
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'nowrap' }}>
                  <DatePicker
                    format="DD MMM YYYY"
                    value={
                      formData.end_date
                        ? (dayjs.isDayjs(formData.end_date) ? formData.end_date : dayjs(formData.end_date))
                        : (formData.end_time
                            ? (dayjs.isDayjs(formData.end_time) ? formData.end_time : dayjs(formData.end_time))
                            : null)
                    }
                    onChange={(value) => handleChange('end_date', value)}
                    style={{
                      flex: '1 1 auto',
                      minWidth: '100px',
                      maxWidth: '100%',
                      borderRadius: '4px',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      fontSize: '12px',
                      height: '32px'
                    }}
                    placeholder="Date"
                    allowClear={false}
                  />
                  <TimePicker
                    format="HH:mm"
                    value={
                      formData.end_time_only !== null && formData.end_time_only !== undefined
                        ? (dayjs.isDayjs(formData.end_time_only) 
                            ? formData.end_time_only 
                            : (formData.end_time_only ? dayjs(formData.end_time_only) : null))
                        : (formData.end_time !== null && formData.end_time !== undefined
                            ? (dayjs.isDayjs(formData.end_time) 
                                ? formData.end_time 
                                : (formData.end_time ? dayjs(formData.end_time) : null))
                            : null)
                    }
                    onChange={(value) => handleChange('end_time_only', value)}
                    minuteStep={5}
                    style={{
                      flex: '0 0 auto',
                      width: '75px',
                      borderRadius: '4px',
                      border: '1px solid #d1d5db',
                      backgroundColor: 'white',
                      fontSize: '12px',
                      height: '32px'
                    }}
                    placeholder="Time"
                    getPopupContainer={(trigger) => trigger.parentElement}
                    popupStyle={{ zIndex: 1050 }}
                    allowClear={false}
                  />
                </div>
              </div>
            </div>

            {/* Row 2: First Good Tablet + Last Good Tablet (only for compression machines) */}
            {formData.activity_type === 'manufacturing' && isCompressionMachine(formData.equipment_id) && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                {/* First Good Tablet */}
                <div style={{ 
                  flex: 1,
                  minWidth: 0,
                  padding: '10px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '6px',
                  border: '1px solid #fcd34d'
                }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#92400e', fontSize: '12px' }}>
                    🔶 First Good Tablet *
                  </label>
                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'nowrap' }}>
                    <DatePicker
                      format="DD MMM YYYY"
                      value={formData.first_good_tablet_date ? (dayjs.isDayjs(formData.first_good_tablet_date) ? formData.first_good_tablet_date : dayjs(formData.first_good_tablet_date)) : null}
                      onChange={(value) => handleChange('first_good_tablet_date', value)}
                      style={{ flex: '1 1 auto', minWidth: '100px', maxWidth: '100%', height: '32px' }}
                      placeholder="Date"
                      allowClear={false}
                    />
                    <TimePicker
                      format="HH:mm"
                      value={formData.first_good_tablet_time ? (dayjs.isDayjs(formData.first_good_tablet_time) ? formData.first_good_tablet_time : dayjs(formData.first_good_tablet_time)) : null}
                      onChange={(value) => handleChange('first_good_tablet_time', value)}
                      minuteStep={5}
                      style={{ flex: '0 0 auto', width: '75px', height: '32px' }}
                      placeholder="Time"
                      allowClear={false}
                      getPopupContainer={(trigger) => trigger.parentElement}
                      popupStyle={{ zIndex: 1050 }}
                    />
                  </div>
                </div>

                {/* Last Good Tablet */}
                <div style={{ 
                  flex: 1,
                  minWidth: 0,
                  padding: '10px',
                  backgroundColor: '#fef3c7',
                  borderRadius: '6px',
                  border: '1px solid #fcd34d'
                }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#92400e', fontSize: '12px' }}>
                    🔶 Last Good Tablet *
                  </label>
                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'nowrap' }}>
                    <DatePicker
                      format="DD MMM YYYY"
                      value={formData.last_good_tablet_date ? (dayjs.isDayjs(formData.last_good_tablet_date) ? formData.last_good_tablet_date : dayjs(formData.last_good_tablet_date)) : null}
                      onChange={(value) => handleChange('last_good_tablet_date', value)}
                      style={{ flex: '1 1 auto', minWidth: '100px', maxWidth: '100%', height: '32px' }}
                      placeholder="Date"
                      allowClear={false}
                    />
                    <TimePicker
                      format="HH:mm"
                      value={formData.last_good_tablet_time ? (dayjs.isDayjs(formData.last_good_tablet_time) ? formData.last_good_tablet_time : dayjs(formData.last_good_tablet_time)) : null}
                      onChange={(value) => handleChange('last_good_tablet_time', value)}
                      minuteStep={5}
                      style={{ flex: '0 0 auto', width: '75px', height: '32px' }}
                      placeholder="Time"
                      allowClear={false}
                      getPopupContainer={(trigger) => trigger.parentElement}
                      popupStyle={{ zIndex: 1050 }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Manufacturing Fields - Simplified (no volume, no batch generation) */}
          {formData.activity_type === 'manufacturing' && (
            <div style={{ 
              padding: '16px', 
              backgroundColor: '#eff6ff', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '1px solid #bfdbfe'
            }}>
              <div style={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
                {/* Product Name */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                    Product Name *
                  </label>
                  <Input
                    placeholder="Enter product name"
                    value={formData.product_name}
                    onChange={(e) => handleChange('product_name', e.target.value)}
                  />
                </div>

                {/* Batch Number */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                    Batch Number *
                  </label>
                  <Input
                    placeholder="Enter batch number"
                    value={formData.batch_number}
                    onChange={(e) => handleChange('batch_number', e.target.value)}
                  />
                </div>

                {/* Duration */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                    Duration (hrs)
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step="0.25"
                    placeholder="e.g. 2.5"
                    value={formData.duration_hrs}
                    onChange={(e) => {
                      const v = e.target.value;
                      handleChange('duration_hrs', v);
                      recalcEndFromDuration(v, formData.start_time);
                    }}
                  />
                  <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                    End time auto-calculates from Start + duration.
                  </div>
                </div>

                {/* Is Split Completed (only for packing machines) */}
                {isPackingEquipment(formData.equipment_id) && (
                  <div>
                    <Checkbox
                      checked={formData.is_split_completed}
                      onChange={(e) => handleChange('is_split_completed', e.target.checked)}
                    >
                      <span style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>Is Split Completed</span>
                    </Checkbox>
                    {!formData.is_split_completed && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#a16207' }}>
                        This activity will be highlighted as Split Pending until it is marked completed.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Cleaning Fields */}
          {formData.activity_type === 'cleaning' && (
            <div style={{ 
              padding: '16px', 
              backgroundColor: '#ecfdf5', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '1px solid #a7f3d0'
            }}>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                    Activity Type *
                  </label>
                  <Select
                    placeholder="Select activity type"
                    value={formData.cleaning_type}
                    onChange={(value) => handleChange('cleaning_type', value)}
                    style={{ width: '100%' }}
                    options={cleaningTypes}
                  />
                </div>
                <div style={{ flex: '1', minWidth: '150px', maxWidth: '200px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                    Duration (hrs) *
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step="0.25"
                    placeholder="e.g. 2.5"
                    value={formData.duration_hrs}
                    onChange={(e) => {
                      const v = e.target.value;
                      handleChange('duration_hrs', v);
                      recalcEndFromDuration(v, formData.start_time);
                    }}
                  />
                  <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>
                    End time auto-calculates
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Maintenance Fields */}
          {formData.activity_type === 'maintenance' && (
            <div style={{ 
              padding: '16px', 
              backgroundColor: '#fffbeb', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '1px solid #fde68a'
            }}>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                    Maintenance Type *
                  </label>
                  <Select
                    placeholder="Select maintenance type"
                    value={formData.maintenance_type}
                    onChange={(value) => handleChange('maintenance_type', value)}
                    style={{ width: '100%' }}
                    options={maintenanceTypes}
                  />
                </div>
                <div style={{ flex: '1', minWidth: '150px', maxWidth: '200px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                    Duration (hrs) *
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step="0.25"
                    placeholder="e.g. 2.5"
                    value={formData.duration_hrs}
                    onChange={(e) => {
                      const v = e.target.value;
                      handleChange('duration_hrs', v);
                      recalcEndFromDuration(v, formData.start_time);
                    }}
                  />
                  <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280' }}>
                    End time auto-calculates
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Remarks */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
              Remarks (Optional)
            </label>
            <TextArea
              placeholder="Enter any additional remarks..."
              value={formData.remarks}
              onChange={(e) => handleChange('remarks', e.target.value)}
              rows={2}
            />
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button onClick={() => {
              setIsModalOpen(false);
              handleReset();
            }}>
              Cancel
            </Button>
            <Button onClick={handleReset}>
              Reset
            </Button>
            <Button 
              type="primary" 
              onClick={handleSubmit}
              loading={submitting}
              style={{ 
                backgroundColor: getActivityColor(formData.activity_type),
                borderColor: getActivityColor(formData.activity_type)
              }}
            >
              {submitting ? 'Logging...' : 'Log Activity'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Read-Only Activity Detail Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              width: '8px', 
              height: '24px', 
              backgroundColor: selectedActivity ? getActivityColor(selectedActivity.activity_type) : '#6b7280',
              borderRadius: '4px'
            }} />
            <span>{isEditMode ? 'Edit Activity' : 'Activity Details'}</span>
          </div>
        }
        open={isDetailModalOpen}
        onCancel={() => {
          setIsDetailModalOpen(false);
          setSelectedActivity(null);
          setIsEditMode(false);
          setEditFormData(null);
        }}
        footer={
          isEditMode ? (
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                setIsEditMode(false);
                setEditFormData(null);
              }}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                loading={submitting}
                onClick={async () => {
                  if (!editFormData || !selectedActivity) return;
                  
                  // Validation
                  if (!editFormData.start_time) {
                    message.error('Please select a start time');
                    return;
                  }
                  if (!editFormData.end_time) {
                    message.error('Please select an end time');
                    return;
                  }
                  if (editFormData.activity_type === 'manufacturing') {
                    if (!editFormData.batch_number) {
                      message.error('Please enter a batch number');
                      return;
                    }
                    if (!editFormData.product_name) {
                      message.error('Please enter a product name');
                      return;
                    }
                    
                    // Additional validation for compression machines
                    if (isCompressionMachine(editFormData.equipment_id)) {
                      if (!editFormData.first_good_tablet_date || !editFormData.first_good_tablet_time) {
                        message.error('Please enter First Good Tablet date and time');
                        return;
                      }
                      if (!editFormData.last_good_tablet_date || !editFormData.last_good_tablet_time) {
                        message.error('Please enter Last Good Tablet date and time');
                        return;
                      }
                    }
                  }
                  if (editFormData.activity_type === 'cleaning' && !editFormData.cleaning_type) {
                    message.error('Please select a cleaning type');
                    return;
                  }
                  if (editFormData.activity_type === 'maintenance' && !editFormData.maintenance_type) {
                    message.error('Please select a maintenance type');
                    return;
                  }
                  
                  setSubmitting(true);
                  try {
                    let payload;
                    
                    if (editFormData.activity_type === 'manufacturing') {
                      payload = {
                        eqp_plan_type: 'Operation',
                        eqp_activity: 'Manufacturing Activity',
                        eqp_id: editFormData.equipment_id,
                        start_date: formatDateTime(editFormData.start_time),
                        end_date: formatDateTime(editFormData.end_time),
                        product_batch: editFormData.batch_number || '',
                        product_name: editFormData.product_name || '',
                        // Only PACKING machines show the checkbox; everything else is always true
                        is_split_completed: isPackingEquipment(editFormData.equipment_id) ? editFormData.is_split_completed : true
                      };
                      
                      // Add compression machine specific fields
                      if (isCompressionMachine(editFormData.equipment_id)) {
                        const firstGoodTablet = editFormData.first_good_tablet_date && editFormData.first_good_tablet_time
                          ? dayjs(editFormData.first_good_tablet_date)
                              .hour(dayjs(editFormData.first_good_tablet_time).hour())
                              .minute(dayjs(editFormData.first_good_tablet_time).minute())
                              .second(0)
                          : null;
                        const lastGoodTablet = editFormData.last_good_tablet_date && editFormData.last_good_tablet_time
                          ? dayjs(editFormData.last_good_tablet_date)
                              .hour(dayjs(editFormData.last_good_tablet_time).hour())
                              .minute(dayjs(editFormData.last_good_tablet_time).minute())
                              .second(0)
                          : null;
                        
                        if (firstGoodTablet) {
                          payload.first_good_tablet = formatDateTime(firstGoodTablet);
                        }
                        if (lastGoodTablet) {
                          payload.last_good_tablet = formatDateTime(lastGoodTablet);
                        }
                      }
                    } else if (editFormData.activity_type === 'cleaning') {
                      payload = {
                        eqp_plan_type: 'Cleaning',
                        eqp_activity: editFormData.cleaning_type || '',
                        eqp_id: editFormData.equipment_id,
                        start_date: formatDateTime(editFormData.start_time),
                        end_date: formatDateTime(editFormData.end_time)
                      };
                    } else {
                      payload = {
                        eqp_plan_type: 'Maintenance',
                        eqp_activity: editFormData.maintenance_type || '',
                        eqp_id: editFormData.equipment_id,
                        start_date: formatDateTime(editFormData.start_time),
                        end_date: formatDateTime(editFormData.end_time)
                      };
                    }
                    
                    const response = await updatePhysicalLogActivity(selectedActivity.id, payload);
                    
                    if (response.success) {
                      message.success('Activity updated successfully!');
                      await loadActivities();
                      setIsDetailModalOpen(false);
                      setSelectedActivity(null);
                      setIsEditMode(false);
                      setEditFormData(null);
                    } else {
                      throw new Error(response.message || 'Failed to update activity');
                    }
                  } catch (err) {
                    console.error('Error updating activity:', err);
                    message.error(err.message || 'Failed to update activity. Please try again.');
                  } finally {
                    setSubmitting(false);
                  }
                }}
                style={{ 
                  backgroundColor: selectedActivity ? getActivityColor(editFormData?.activity_type || selectedActivity.activity_type) : '#3b82f6',
                  borderColor: selectedActivity ? getActivityColor(editFormData?.activity_type || selectedActivity.activity_type) : '#3b82f6'
                }}
              >
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
              <Button 
                danger
                loading={deleting}
                onClick={async () => {
                  if (!selectedActivity) return;
                  
                  Modal.confirm({
                    title: 'Delete Activity',
                    content: 'Are you sure you want to delete this activity? This action cannot be undone.',
                    okText: 'Delete',
                    okType: 'danger',
                    cancelText: 'Cancel',
                    onOk: async () => {
                      setDeleting(true);
                      try {
                        const response = await deletePhysicalLogActivity(selectedActivity.id);
                        
                        if (response.success) {
                          message.success('Activity deleted successfully!');
                          await loadActivities();
                          setIsDetailModalOpen(false);
                          setSelectedActivity(null);
                        } else {
                          throw new Error(response.message || 'Failed to delete activity');
                        }
                      } catch (err) {
                        console.error('Error deleting activity:', err);
                        message.error(err.message || 'Failed to delete activity. Please try again.');
                      } finally {
                        setDeleting(false);
                      }
                    }
                  });
                }}
              >
                Delete
              </Button>
              <div style={{ display: 'flex', gap: '12px' }}>
                <Button onClick={() => {
                  setIsDetailModalOpen(false);
                  setSelectedActivity(null);
                }}>
                  Close
                </Button>
                <Button 
                  type="primary"
                  onClick={() => {
                    if (!selectedActivity) return;
                    // Initialize edit form with current activity data
                    setEditFormData({
                      equipment_id: selectedActivity.eqp_id || selectedActivity.equipment_id,
                      equipment_name: selectedActivity.equipment_name,
                      activity_type: selectedActivity.activity_type,
                      start_time: dayjs(selectedActivity.start_time),
                      end_time: selectedActivity.end_time ? dayjs(selectedActivity.end_time) : null,
                      batch_number: selectedActivity.batch_number || selectedActivity.product_batch || '',
                      product_name: selectedActivity.product_name || '',
                      cleaning_type: selectedActivity.cleaning_type || selectedActivity.eqp_activity || null,
                      maintenance_type: selectedActivity.maintenance_type || selectedActivity.eqp_activity || null,
                      remarks: selectedActivity.remarks || '',
                      // Compression machine specific fields
                      first_good_tablet_date: selectedActivity.first_good_tablet ? dayjs(selectedActivity.first_good_tablet) : null,
                      first_good_tablet_time: selectedActivity.first_good_tablet ? dayjs(selectedActivity.first_good_tablet) : null,
                      last_good_tablet_date: selectedActivity.last_good_tablet ? dayjs(selectedActivity.last_good_tablet) : null,
                      last_good_tablet_time: selectedActivity.last_good_tablet ? dayjs(selectedActivity.last_good_tablet) : null,
                      // Packing machine specific field
                      is_split_completed: selectedActivity.is_split_completed !== false
                    });
                    setIsEditMode(true);
                  }}
                >
                  Edit
                </Button>
              </div>
            </div>
          )
        }
        width={550}
        destroyOnClose
      >
        {selectedActivity && (
          <div style={{ padding: '16px 0' }}>
            {isEditMode && editFormData ? (
              /* Edit Mode Form */
              <>
                {/* Equipment Info (read-only) */}
                <div style={{ 
                  padding: '12px 16px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '8px',
                  marginBottom: '20px'
                }}>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Equipment</div>
                  <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '16px' }}>
                    {selectedActivity.equipment_name || selectedActivity.equipment_id}
                  </div>
                </div>

                {/* Activity Type Selection */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                    Activity Type *
                  </label>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {activityTypes.map(type => (
                      <div
                        key={type.value}
                        onClick={() => setEditFormData(prev => ({ ...prev, activity_type: type.value }))}
                        style={{
                          padding: '10px 20px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          border: editFormData.activity_type === type.value ? `2px solid ${type.color}` : '2px solid #e5e7eb',
                          backgroundColor: editFormData.activity_type === type.value ? `${type.color}15` : 'white',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        <div style={{ width: '14px', height: '14px', borderRadius: '4px', backgroundColor: type.color }} />
                        <span style={{ 
                          fontWeight: editFormData.activity_type === type.value ? '600' : '500',
                          color: editFormData.activity_type === type.value ? type.color : '#374151',
                          fontSize: '14px'
                        }}>
                          {type.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Start & End Date Time */}
                <div style={{ marginBottom: '20px' }}>
                  {/* Row 1: Starts + Ends (always) */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch', marginBottom: editFormData.activity_type === 'manufacturing' && isCompressionMachine(editFormData.equipment_id) ? '8px' : '0' }}>
                    {/* Starts Card */}
                    <div style={{ 
                      flex: 1,
                      minWidth: 0,
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '10px',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '12px' }}>
                        Starts
                      </label>
                      <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'nowrap' }}>
                        <DatePicker
                          format="DD MMM YYYY"
                          value={editFormData.start_time}
                          onChange={(value) => {
                            if (value && editFormData.start_time) {
                              const newStart = editFormData.start_time
                                .year(value.year())
                                .month(value.month())
                                .date(value.date());
                              setEditFormData(prev => ({ ...prev, start_time: newStart }));
                            } else {
                              setEditFormData(prev => ({ ...prev, start_time: value }));
                            }
                          }}
                          style={{ flex: '1 1 auto', minWidth: '100px', height: '32px' }}
                          allowClear={false}
                        />
                        <TimePicker
                          format="HH:mm"
                          value={editFormData.start_time}
                          onChange={(value) => {
                            if (value && editFormData.start_time) {
                              const newStart = editFormData.start_time
                                .hour(value.hour())
                                .minute(value.minute());
                              setEditFormData(prev => ({ ...prev, start_time: newStart }));
                            } else {
                              setEditFormData(prev => ({ ...prev, start_time: value }));
                            }
                          }}
                          minuteStep={5}
                          style={{ width: '75px', height: '32px' }}
                          allowClear={false}
                        />
                      </div>
                    </div>

                    {/* Ends Card */}
                    <div style={{ 
                      flex: 1,
                      minWidth: 0,
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '10px',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '12px' }}>
                        Ends
                      </label>
                      <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'nowrap' }}>
                        <DatePicker
                          format="DD MMM YYYY"
                          value={editFormData.end_time}
                          onChange={(value) => {
                            if (value && editFormData.end_time) {
                              const newEnd = editFormData.end_time
                                .year(value.year())
                                .month(value.month())
                                .date(value.date());
                              setEditFormData(prev => ({ ...prev, end_time: newEnd }));
                            } else {
                              setEditFormData(prev => ({ ...prev, end_time: value }));
                            }
                          }}
                          style={{ flex: '1 1 auto', minWidth: '100px', height: '32px' }}
                          allowClear={false}
                        />
                        <TimePicker
                          format="HH:mm"
                          value={editFormData.end_time}
                          onChange={(value) => {
                            if (value && editFormData.end_time) {
                              const newEnd = editFormData.end_time
                                .hour(value.hour())
                                .minute(value.minute());
                              setEditFormData(prev => ({ ...prev, end_time: newEnd }));
                            } else {
                              setEditFormData(prev => ({ ...prev, end_time: value }));
                            }
                          }}
                          minuteStep={5}
                          style={{ width: '75px', height: '32px' }}
                          allowClear={false}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Row 2: First Good Tablet + Last Good Tablet (only for compression machines) */}
                  {editFormData.activity_type === 'manufacturing' && isCompressionMachine(editFormData.equipment_id) && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                      {/* First Good Tablet */}
                      <div style={{ 
                        flex: 1,
                        minWidth: 0,
                        padding: '10px',
                        backgroundColor: '#fef3c7',
                        borderRadius: '6px',
                        border: '1px solid #fcd34d'
                      }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#92400e', fontSize: '12px' }}>
                          🔶 First Good Tablet *
                        </label>
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'nowrap' }}>
                          <DatePicker
                            format="DD MMM YYYY"
                            value={editFormData.first_good_tablet_date ? (dayjs.isDayjs(editFormData.first_good_tablet_date) ? editFormData.first_good_tablet_date : dayjs(editFormData.first_good_tablet_date)) : null}
                            onChange={(value) => setEditFormData(prev => ({ ...prev, first_good_tablet_date: value }))}
                            style={{ flex: '1 1 auto', minWidth: '100px', maxWidth: '100%', height: '32px' }}
                            placeholder="Date"
                            allowClear={false}
                          />
                          <TimePicker
                            format="HH:mm"
                            value={editFormData.first_good_tablet_time ? (dayjs.isDayjs(editFormData.first_good_tablet_time) ? editFormData.first_good_tablet_time : dayjs(editFormData.first_good_tablet_time)) : null}
                            onChange={(value) => setEditFormData(prev => ({ ...prev, first_good_tablet_time: value }))}
                            minuteStep={5}
                            style={{ flex: '0 0 auto', width: '75px', height: '32px' }}
                            placeholder="Time"
                            allowClear={false}
                            getPopupContainer={(trigger) => trigger.parentElement}
                            popupStyle={{ zIndex: 1050 }}
                          />
                        </div>
                      </div>

                      {/* Last Good Tablet */}
                      <div style={{ 
                        flex: 1,
                        minWidth: 0,
                        padding: '10px',
                        backgroundColor: '#fef3c7',
                        borderRadius: '6px',
                        border: '1px solid #fcd34d'
                      }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#92400e', fontSize: '12px' }}>
                          🔶 Last Good Tablet *
                        </label>
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'nowrap' }}>
                          <DatePicker
                            format="DD MMM YYYY"
                            value={editFormData.last_good_tablet_date ? (dayjs.isDayjs(editFormData.last_good_tablet_date) ? editFormData.last_good_tablet_date : dayjs(editFormData.last_good_tablet_date)) : null}
                            onChange={(value) => setEditFormData(prev => ({ ...prev, last_good_tablet_date: value }))}
                            style={{ flex: '1 1 auto', minWidth: '100px', maxWidth: '100%', height: '32px' }}
                            placeholder="Date"
                            allowClear={false}
                          />
                          <TimePicker
                            format="HH:mm"
                            value={editFormData.last_good_tablet_time ? (dayjs.isDayjs(editFormData.last_good_tablet_time) ? editFormData.last_good_tablet_time : dayjs(editFormData.last_good_tablet_time)) : null}
                            onChange={(value) => setEditFormData(prev => ({ ...prev, last_good_tablet_time: value }))}
                            minuteStep={5}
                            style={{ flex: '0 0 auto', width: '75px', height: '32px' }}
                            placeholder="Time"
                            allowClear={false}
                            getPopupContainer={(trigger) => trigger.parentElement}
                            popupStyle={{ zIndex: 1050 }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Manufacturing Fields */}
                {editFormData.activity_type === 'manufacturing' && (
                  <div style={{ 
                    padding: '16px', 
                    backgroundColor: '#eff6ff', 
                    borderRadius: '8px', 
                    marginBottom: '20px',
                    border: '1px solid #bfdbfe'
                  }}>
                    <div style={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                          Product Name *
                        </label>
                        <Input
                          placeholder="Enter product name"
                          value={editFormData.product_name}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, product_name: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                          Batch Number *
                        </label>
                        <Input
                          placeholder="Enter batch number"
                          value={editFormData.batch_number}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, batch_number: e.target.value }))}
                        />
                      </div>

                      {/* Is Split Completed (only for packing machines) */}
                      {isPackingEquipment(editFormData.equipment_id) && (
                        <div>
                          <Checkbox
                            checked={editFormData.is_split_completed}
                            onChange={(e) => setEditFormData(prev => ({ ...prev, is_split_completed: e.target.checked }))}
                          >
                            <span style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>Is Split Completed</span>
                          </Checkbox>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Cleaning Fields */}
                {editFormData.activity_type === 'cleaning' && (
                  <div style={{ 
                    padding: '16px', 
                    backgroundColor: '#ecfdf5', 
                    borderRadius: '8px', 
                    marginBottom: '20px',
                    border: '1px solid #a7f3d0'
                  }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                        Activity Type *
                      </label>
                      <Select
                        placeholder="Select activity type"
                        value={editFormData.cleaning_type}
                        onChange={(value) => setEditFormData(prev => ({ ...prev, cleaning_type: value }))}
                        style={{ width: '100%' }}
                        options={cleaningTypes}
                      />
                    </div>
                  </div>
                )}

                {/* Maintenance Fields */}
                {editFormData.activity_type === 'maintenance' && (
                  <div style={{ 
                    padding: '16px', 
                    backgroundColor: '#fffbeb', 
                    borderRadius: '8px', 
                    marginBottom: '20px',
                    border: '1px solid #fde68a'
                  }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                        Maintenance Type *
                      </label>
                      <Select
                        placeholder="Select maintenance type"
                        value={editFormData.maintenance_type}
                        onChange={(value) => setEditFormData(prev => ({ ...prev, maintenance_type: value }))}
                        style={{ width: '100%' }}
                        options={maintenanceTypes}
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Read-Only View */
              <>
                {/* Equipment Info */}
                <div style={{ 
                  padding: '12px 16px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '8px',
                  marginBottom: '20px'
                }}>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Equipment</div>
                  <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '16px' }}>
                    {selectedActivity.equipment_name || selectedActivity.equipment_id}
                  </div>
                </div>

                {/* Activity Type */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Activity Type</div>
                  <div style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    padding: '6px 12px',
                    backgroundColor: `${getActivityColor(selectedActivity.activity_type)}15`,
                    borderRadius: '6px',
                    border: `1px solid ${getActivityColor(selectedActivity.activity_type)}`
                  }}>
                    <div style={{ 
                      width: '12px', 
                      height: '12px', 
                      borderRadius: '3px', 
                      backgroundColor: getActivityColor(selectedActivity.activity_type) 
                    }} />
                    <span style={{ 
                      fontWeight: '600', 
                      color: getActivityColor(selectedActivity.activity_type),
                      textTransform: 'capitalize'
                    }}>
                      {selectedActivity.activity_type}
                    </span>
                  </div>
                </div>

                {/* Time Range */}
                <div style={{ 
                  display: 'flex', 
                  gap: '16px', 
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Start Time</div>
                    <div style={{ fontWeight: '500', color: '#1f2937' }}>
                      {dayjs(selectedActivity.start_time).format('DD MMM YYYY HH:mm')}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>End Time</div>
                    <div style={{ fontWeight: '500', color: '#1f2937' }}>
                      {selectedActivity.end_time 
                        ? dayjs(selectedActivity.end_time).format('DD MMM YYYY HH:mm')
                        : 'Ongoing'}
                    </div>
                  </div>
                  <div style={{ flex: 0.6 }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Duration</div>
                    <div style={{ fontWeight: '600', color: '#059669' }}>
                      {selectedActivity.end_time 
                        ? `${Math.round(dayjs(selectedActivity.end_time).diff(dayjs(selectedActivity.start_time), 'hour', true) * 10) / 10} hrs`
                        : '-'}
                    </div>
                  </div>
                </div>

                {/* Manufacturing specific fields */}
                {selectedActivity.activity_type === 'manufacturing' && (
                  <div style={{ 
                    padding: '12px',
                    backgroundColor: '#eff6ff',
                    borderRadius: '6px',
                    marginBottom: '16px',
                    border: '1px solid #bfdbfe'
                  }}>
                    {selectedActivity.product_name && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Product Name</div>
                        <div style={{ fontWeight: '500', color: '#1f2937' }}>{selectedActivity.product_name}</div>
                      </div>
                    )}
                    {selectedActivity.batch_number && (
                      <div style={{ marginBottom: isCompressionMachine(selectedActivity.equipment_id) ? '12px' : '0' }}>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Batch Number</div>
                        <div style={{ fontWeight: '500', color: '#1f2937' }}>{selectedActivity.batch_number}</div>
                      </div>
                    )}

                    {/* Split Completed (only for packing machines) */}
                    {isPackingEquipment(selectedActivity.equipment_id) && (
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Split Completed</div>
                        <div style={{ fontWeight: '600', color: selectedActivity.is_split_completed === false ? '#a16207' : '#059669' }}>
                          {selectedActivity.is_split_completed === false ? 'No (Split Pending)' : 'Yes'}
                        </div>
                      </div>
                    )}

                    {/* Compression Machine Specific Fields */}
                    {isCompressionMachine(selectedActivity.equipment_id) && (
                      <div style={{ 
                        marginTop: '12px',
                        paddingTop: '12px',
                        borderTop: '1px dashed #bfdbfe'
                      }}>
                        <div style={{ display: 'flex', gap: '16px' }}>
                          {selectedActivity.first_good_tablet && (
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '4px', fontWeight: '600' }}>
                                🔶 First Good Tablet
                              </div>
                              <div style={{ fontWeight: '500', color: '#1f2937' }}>
                                {dayjs(selectedActivity.first_good_tablet).format('DD MMM YYYY HH:mm')}
                              </div>
                            </div>
                          )}
                          {selectedActivity.last_good_tablet && (
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '4px', fontWeight: '600' }}>
                                🔶 Last Good Tablet
                              </div>
                              <div style={{ fontWeight: '500', color: '#1f2937' }}>
                                {dayjs(selectedActivity.last_good_tablet).format('DD MMM YYYY HH:mm')}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Cleaning specific fields */}
                {selectedActivity.activity_type === 'cleaning' && selectedActivity.cleaning_type && (
                  <div style={{ 
                    padding: '12px',
                    backgroundColor: '#ecfdf5',
                    borderRadius: '6px',
                    marginBottom: '16px',
                    border: '1px solid #a7f3d0'
                  }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Cleaning Type</div>
                    <div style={{ fontWeight: '500', color: '#1f2937' }}>{selectedActivity.cleaning_type}</div>
                  </div>
                )}

                {/* Maintenance specific fields */}
                {selectedActivity.activity_type === 'maintenance' && selectedActivity.maintenance_type && (
                  <div style={{ 
                    padding: '12px',
                    backgroundColor: '#fffbeb',
                    borderRadius: '6px',
                    marginBottom: '16px',
                    border: '1px solid #fde68a'
                  }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Maintenance Type</div>
                    <div style={{ fontWeight: '500', color: '#1f2937' }}>{selectedActivity.maintenance_type}</div>
                  </div>
                )}

                {/* Remarks */}
                {selectedActivity.remarks && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Remarks</div>
                    <div style={{ 
                      padding: '8px 12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '6px',
                      color: '#374151',
                      fontSize: '14px'
                    }}>
                      {selectedActivity.remarks}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Download Activities Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DownloadOutlined style={{ color: '#3b82f6' }} />
            <span>Download Physical Log Activities</span>
          </div>
        }
        open={isDownloadModalOpen}
        onCancel={() => {
          if (!downloading) {
            setIsDownloadModalOpen(false);
            setDownloadStartDate(null);
            setDownloadEndDate(null);
          }
        }}
        footer={null}
        width={450}
        maskClosable={!downloading}
        closable={!downloading}
      >
        <div style={{ padding: '16px 0' }}>
          <div style={{ marginBottom: '20px' }}>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 16px 0' }}>
              Select a date range to download physical log activities as an Excel file.
            </p>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontWeight: '500', 
                color: '#374151',
                fontSize: '14px'
              }}>
                Start Date <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <DatePicker
                value={downloadStartDate}
                onChange={(date) => setDownloadStartDate(date)}
                format="DD-MMM-YYYY"
                style={{ width: '100%' }}
                disabled={downloading}
                placeholder="Select start date"
              />
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontWeight: '500', 
                color: '#374151',
                fontSize: '14px'
              }}>
                End Date <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <DatePicker
                value={downloadEndDate}
                onChange={(date) => setDownloadEndDate(date)}
                format="DD-MMM-YYYY"
                style={{ width: '100%' }}
                disabled={downloading}
                placeholder="Select end date"
              />
            </div>
          </div>
          
          {/* Progress indicator */}
          {downloading && downloadProgress.total > 0 && (
            <div style={{ 
              marginTop: '20px',
              padding: '12px 16px',
              backgroundColor: '#eff6ff',
              borderRadius: '6px',
              border: '1px solid #bfdbfe'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Spin size="small" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', color: '#1e40af', fontWeight: '500' }}>
                    Fetching activities...
                  </div>
                  <div style={{ fontSize: '12px', color: '#3b82f6', marginTop: '4px' }}>
                    Day {downloadProgress.current} of {downloadProgress.total}
                  </div>
                  <div style={{ 
                    marginTop: '8px',
                    height: '6px',
                    backgroundColor: '#dbeafe',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      backgroundColor: '#3b82f6',
                      borderRadius: '3px',
                      width: `${(downloadProgress.current / downloadProgress.total) * 100}%`,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Action buttons */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '12px',
            marginTop: '24px',
            paddingTop: '16px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <Button 
              onClick={() => {
                setIsDownloadModalOpen(false);
                setDownloadStartDate(null);
                setDownloadEndDate(null);
              }}
              disabled={downloading}
            >
              Cancel
            </Button>
            <Button 
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadActivities}
              loading={downloading}
              disabled={!downloadStartDate || !downloadEndDate}
            >
              {downloading ? 'Downloading...' : 'Download Excel'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PhysicalLogEntry;
